/* Document upload — calm operational error mapping.
 *
 * The contract:
 *   • Backend always emits a structured `{code, message, field?}` detail
 *     for every failure (see /app/backend/routes_documents.py).
 *   • Frontend never surfaces "Upload failed" or generic copy.
 *
 * `uploadErrorPresentation(err, filename)` returns a `{ title, description }`
 * pair the caller passes straight to `toast.error(...)`. The toast title
 * reflects the failure code (e.g. "File too large", "Unsupported file
 * type", "Duplicate name in folder") so the operator can act without
 * reading the description.
 *
 * Also exports `clientPreflight(file)` — a calm client-side gate that
 * mirrors the server's hard rules (25 MB, MIME allow-list, non-empty)
 * so users get an instant inline error before any bytes go on the wire.
 */
import { apiError, apiErrorCode } from "./api";

// Mirror backend `storage_tenancy.ALLOWED_MIME_PREFIXES` + MAX_FILE_BYTES.
// Keeping these in sync gives us identical preflight messages on both sides.
export const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;
export const ACCEPTED_HUMAN = "PDF, Word, Excel, OpenOffice, images, ZIP, CSV or plain text";

const ALLOWED_PREFIXES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument",
  "application/vnd.ms-excel",
  "application/vnd.oasis.opendocument",
  "application/zip",
  "image/",
  "text/plain",
  "text/csv",
];

const EXT_TO_MIME = {
  pdf:  "application/pdf",
  doc:  "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xls:  "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ppt:  "application/vnd.ms-powerpoint",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  csv:  "text/csv",
  txt:  "text/plain",
  zip:  "application/zip",
  png:  "image/png",
  jpg:  "image/jpeg",
  jpeg: "image/jpeg",
  webp: "image/webp",
  heic: "image/heic",
  heif: "image/heif",
};

const humanSize = (n) => {
  const units = ["B", "KB", "MB", "GB"];
  let v = n;
  for (const u of units) {
    if (v < 1024 || u === "GB") return `${u === "B" ? Math.round(v) : v.toFixed(1).replace(/\.0$/, "")} ${u}`;
    v /= 1024;
  }
  return `${v.toFixed(1)} GB`;
};

/** Resolve the MIME for a File — browsers leave `type` empty for some
 *  uploads (drag-drop from Windows, etc.) so fall back to extension. */
const resolveMime = (file) => {
  const t = (file?.type || "").toLowerCase();
  if (t) return t.split(";")[0].trim();
  const name = (file?.name || "").toLowerCase();
  const m = name.match(/\.([a-z0-9]+)$/);
  if (m && EXT_TO_MIME[m[1]]) return EXT_TO_MIME[m[1]];
  return "";
};

/** Returns `{ ok: true }` when the file passes, otherwise an operational
 *  reason string the caller can drop straight into a toast description. */
export const clientPreflight = (file) => {
  if (!file) return { ok: false, code: "missing_file", reason: "No file was selected." };
  if (file.size === 0) {
    return { ok: false, code: "empty_file", title: "Empty file",
      reason: "That file is empty. Please choose a file with content." };
  }
  if (file.size > MAX_UPLOAD_BYTES) {
    const maxMb = Math.floor(MAX_UPLOAD_BYTES / (1024 * 1024));
    return {
      ok: false, code: "file_too_large", title: "File too large",
      reason: `That file is ${humanSize(file.size)} — the limit is ${maxMb} MB. Please compress or split it.`,
    };
  }
  const mime = resolveMime(file);
  const matches = mime && ALLOWED_PREFIXES.some((p) => mime.startsWith(p));
  if (!matches) {
    return {
      ok: false, code: "unsupported_file_type", title: "Unsupported file type",
      reason: `That file type isn't supported. Accepted: ${ACCEPTED_HUMAN}.`,
    };
  }
  return { ok: true };
};

const TITLES_BY_CODE = {
  empty_file:              "Empty file",
  file_too_large:          "File too large",
  unsupported_file_type:   "Unsupported file type",
  duplicate_document_name: "Duplicate name in folder",
  invalid_folder:          "Folder name invalid",
  upload_interrupted:      "Upload interrupted",
  storage_unconfigured:    "Storage unavailable",
  storage_writes_disabled: "Disabled in preview",
  storage_upload_failed:   "Storage rejected the file",
  document_save_failed:    "Save failed",
  deal_not_found:          "Deal not found",
  document_not_found:      "Document not found",
  workspace_missing:       "Workspace not set",
};

/** Map an axios upload error → calm toast payload. */
export const uploadErrorPresentation = (err, filename) => {
  const description = apiError(err);
  const code = apiErrorCode(err);
  const s = err?.response?.status;

  let title = TITLES_BY_CODE[code];
  if (!title) {
    if (s === 401) title = "Session expired";
    else if (s === 403) title = "Not permitted";
    else if (s === 413) title = "File too large";
    else if (s === 415) title = "Unsupported file type";
    else if (s === 429) title = "Too many uploads";
    else if (s === 502 || s === 503) title = "Storage unavailable";
    else if (s >= 500) title = "Upload failed";
    else if (err?.code === "ERR_NETWORK") title = "Lost connection";
    else if (err?.code === "ECONNABORTED") title = "Upload timed out";
    else title = "Could not upload";
  }
  return {
    title: filename ? `${title} · ${filename}` : title,
    description,
    code,
  };
};
